import api from '.';
import debug from '../debug';
import { decrypt, encrypt } from '../secure';
import { v4 as uuidv4 } from 'uuid';
import { APP_ID, APP_VERSION, getXApiKeyByEnv } from '../constants';

const authorizationExcludedRoutes = [
  'appclicknew',
  'sendotplogin',
  'verifyotplogin',
  'dashboardmenus/sso/validate-token',
  'hypervergeToken',
  'customeronboarding/getkeys',
  // DOCS: Limit Enhancement 2.0 routes
  'validatetoken',
  'perfiosforweb',
  'getbankforweb',
  'getprefferedoptionofbankfoeweb',
  's3savefile-old',
  'docuploadforweb',
  // DOCS: GPay routes
  'customeronboarding/createcustomer',
];

/**
 * API Client for Simple and Encrypt-Decrypt API calls
 * @param {boolean} isEncrypted - true if the API call is encrypted
 * @param {boolean} isExternalBaseUrl - true if the API endpoint is external than conventional API endpoint
 * @param {string} externalBaseUrl - full endpoint URL like https://example.com/some-random-api
 * @param {number} customerId - used for key generation used for encryption and decryption, 'null' for intial API calls like verifyOtp, sendOtp, etc
 * @param {string} route - API route like api/v1/verifyOtp (without base URL,Without leading slash)
 * @param {object} payload - Payload to be posted to the API Endpoint (In encrypted API's case, payload will be stringified and encrypted)
 * @param {object} headers - Headers used to pass as the request headers like (authorization, customerId, Authorization etc)
 * @param {boolean} otherPayloadDetailsPresent - true if the payload contains other details like requestId, appVersion, appId, etc (only true for encrypted API's)
 * @param {string} method - "GET" or "POST" or "PUT" or "DELETE"
 * @param {Function} successFn - Single param callback takes decrypted response object as param
 * @param {Function} errorFn -  Single param callback takes error object as param
 * @param {Function} finallyFn - No param, finally clause in API call
 */
const SecureClient = ({
  isEncrypted = false,
  isExternalBaseUrl = false,
  externalBaseUrl = '',
  customerId = null,
  route = '',
  payload = {},
  otherPayloadDetailsPresent = false,
  headers = {},
  method = 'POST',
  successFn = (res) => {
    debug('WATCH', `Response: `, res);
  },
  errorFn = (err) => {
    debug('ERROR', `Error: `, err);
  },
  finallyFn = () => {
    debug('WATCH', `Finally: `);
  },
}) => {
  const apiLoadingStarted = new Date().getTime();
  let APIPayloadToPost = {},
    APIEndptToPost = route;
  if (isEncrypted) {
    const encryptedPayload = {
      data: encrypt(customerId, payload),
    };
    if (otherPayloadDetailsPresent) {
      encryptedPayload['appId'] = APP_ID;
      encryptedPayload['appVersion'] = APP_VERSION;
      encryptedPayload['requestId'] = uuidv4();
    }
    APIPayloadToPost = encryptedPayload;
  } else {
    APIPayloadToPost = payload;
  }
  if (isExternalBaseUrl) {
    APIEndptToPost = externalBaseUrl;
  }
  try {
    const { authorization = null, Authorization = null } = headers;
    if (
      !Authorization &&
      !authorization &&
      !authorizationExcludedRoutes.includes(route)
    ) {
      debug('ERROR', `[SECURE-CLIENT] Authorization null, 401 Error`);
    }
  } catch (err) {}
  debug('REQUEST', ` ${method} /${route} `, payload);

  localStorage.setItem(
    `custId_${route.split('/').pop()}`,
    customerId ? customerId : null
  );

  api(APIEndptToPost, APIPayloadToPost, method, {
    ...headers,
    'x-api-key': getXApiKeyByEnv(),
  })
    .then((res) => {
      let decrypted = {};

      if (isEncrypted) {
        console.log('resEncStr: ', res.data);
        let resEncStr = res.data && res.data.response;

        debug('RESPONSE', `ENC ${method} /${route} :`, {
          resEncStr,
          customerId,
        });
        decrypted = decrypt(customerId, resEncStr);
        try {
          const { statusCode, statusDes, statusMsg } = decrypted;
          if (statusCode >= 3000 && statusCode <= 3999) {
            const inactiveCustomerError = {
              statusCode,
              statusDes,
              statusMsg,
            };
            localStorage.setItem(
              'inactiveCustomerError',
              JSON.stringify(inactiveCustomerError)
            );
            window.location = '/inactive-customer';
          }
        } catch (err) {
          console.log('Inactive Customer [3000-3999]: ', err);
        }

        debug('RESPONSE', `DEC ${method} /${route} `, decrypted);
      } else {
        decrypted = res.data;
        debug('RESPONSE', `NON ${method} /${route} `, decrypted);
      }
      successFn(decrypted);
    })
    .catch((err) => {
      debug('ERROR', err);
      console.log('err: ', err);
      const res = err?.response;
      if (res && res?.status === 503) {
        // DOCS: Check for disaster recovery changes here
        const disasterRecoveryState = {
          statusCode: res?.status,
          statusMsg: res?.data?.statusMsg,
          statusDes: res?.data?.statusDes,
        };
        localStorage.setItem(
          'disasterRecoveryState',
          JSON.stringify(disasterRecoveryState)
        );
        window.location = '/under-maintainance';
      }

      errorFn(err);
    })
    .finally(() => {
      const apiLoadingFinished = new Date().getTime();
      const timeTakenByAPI = apiLoadingFinished - apiLoadingStarted;
      debug('WATCH', `END ${method} /${route}`, timeTakenByAPI, 'ms');
      if (finallyFn) finallyFn();
    });
};

export default SecureClient;
